import * as AvatarPrimitive from "@radix-ui/react-avatar";
import * as React from "react";

import { cn } from "@/lib/utils";

const Avatar = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    className={cn(
      "relative flex h-10 w-10 shrink-0 overflow-hidden rounded-lg",
      className,
    )}
    {...props}
  />
));
Avatar.displayName = AvatarPrimitive.Root.displayName;

const AvatarImage = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    className={cn("aspect-square h-full w-full", className)}
    {...props}
  />
));
AvatarImage.displayName = AvatarPrimitive.Image.displayName;

const AvatarFallback = React.forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Fallback>,
  React.ComponentPropsWithoutRef<typeof AvatarPrimitive.Fallback>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Fallback
    ref={ref}
    className={cn(
      "flex h-full w-full items-center justify-center rounded-full bg-muted",
      className,
    )}
    {...props}
  />
));
AvatarFallback.displayName = AvatarPrimitive.Fallback.displayName;

export { Avatar, AvatarImage, AvatarFallback };

type TProps = {
  src?: string;
  name: string;
  bg?: "none" | string;
  size?: string | number;
  shape?: "rounded" | "radius-md";
  className?: string;
};

export const SAvatar = ({
  bg = "#E1E6FF",
  name,
  src,
  size,
  shape,
  className,
}: TProps): React.JSX.Element => {
  const getCharacters = (name: string): string => {
    const words = name?.split(" ");
    if (words.length > 1) {
      const charW1 = words[0]?.charAt(0);
      const charW2 = words[1]?.charAt(0);

      return (charW1 || "U") + (charW2 || "N");
    }

    const [w1] = words;

    if (w1 && w1.length > 1) {
      return w1?.charAt(0) + w1?.charAt(1);
    }

    return w1?.charAt(0) || "U";
  };

  const isValidSrc = src?.startsWith("https");

  return (
    <Avatar
      style={{
        width: size || "40px",
        height: size || "40px",
        aspectRatio: "1 / 1",
      }}
      className={cn(shape === "radius-md" && "rounded-md", className)}
      title={name || "Avatar"}
    >
      {isValidSrc ? (
        <img
          src={src}
          width={1920}
          height={1080}
          style={{
            width: size || "40px",
            height: size || "40px",
          }}
          alt={`${name || "User"}'s Avatar`}
          className={cn(
            "aspect-square h-full w-full object-cover",
            shape === "radius-md" && "rounded-md",
          )}
        />
      ) : (
        <div
          style={{
            backgroundColor: bg || "#E1E6FF",
            width: size || "40px",
            height: size || "40px",
            fontSize: `${(typeof size === "number" ? size : parseFloat(String(size)) || 40) / 2.5}px`,
          }}
          className='flex items-center justify-center text-lg text-black uppercase'
          title={name || "Avatar"}
        >
          {getCharacters(name || "undefined")}
        </div>
      )}
    </Avatar>
  );
};
